import { memo, ReactNode, useRef } from "react";

import { ShiftSelectionAction } from "components/BulkActions/useShiftSelectionManager";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper, { TableWrapperProps } from "ds/components/Table/Wrapper";

import { TableHeadInteractiveColumnProps } from "../Table/Head/InteractiveColumn";
import { TableSettingsDrawerProps } from "../Table/Settings/DrawerContent";
import { SelectAllOption } from "../Table/types";
import TableSimpleHead from "./Head";
import TableSimpleLoadMoreRow from "./LoadMoreRow";
import TableSimpleRows from "./Rows";
import TableSimpleVirtualizedRows from "./VirtualizedRows";
import { LOAD_MORE_ROOT_MARGIN } from "./constants";
import {
  StickyRightPredicate,
  TableCellPerColumn,
  TableSimpleDataItem,
  TableSimpleSelectionStore,
  TableSimpleStore,
} from "./types";

type TableSimpleProps<T extends TableSimpleDataItem> = {
  data: T[];
  store: TableSimpleStore;
  selectionStore?: TableSimpleSelectionStore;
  selectionOptions?: SelectAllOption[];
  shiftSelectionAction?: ShiftSelectionAction;
  cellPerColumn: TableCellPerColumn<T>;
  ariaLabel: string;
  loading?: boolean;
  rowsPerPage?: number;
  itemIdKey?: string;
  wrapperClassName?: string;
  emptyState?: ReactNode;
  virtualize?: boolean;
  onLoadMore?: () => void;
  hideSettings?: boolean;
  stickyRight?: StickyRightPredicate;
  activeRowId?: string;
} & Pick<TableSettingsDrawerProps, "trackAnalytics"> &
  Pick<
    TableHeadInteractiveColumnProps,
    "analyticsPage" | "filtersStorageKey" | "sortCallback" | "sortConfig" | "externalSortManagement"
  > &
  Pick<TableWrapperProps, "bottomOffset"> &
  Required<Pick<TableWrapperProps, "hasPagination">>;

/**
 * TableSimple is a plug and play table component that can be used for most use cases.
 *
 * @param data - The data to display in the table.
 * @param store - The store to use for the table. Defaults to an empty store.
 * @param selectionStore - The store to use for the selection. Defaults to an empty store.
 * @param selectionOptions - The options for the selection. Defaults to an empty array.
 * @param shiftSelectionAction - The action to use for the shift selection. Defaults to undefined.
 * @param ariaLabel - The aria label for the table.
 * @param loading - Whether the table is loading. Defaults to false.
 * @param rowsPerPage - The number of rows per page. Defaults to 50.
 * @param sortCallback - The callback to use for the sort. Defaults to undefined.
 * @param analyticsPage - The analytics page for the table. Defaults to undefined.
 * @param filtersStorageKey - The storage key for the filters. Defaults to undefined.
 * @param trackAnalytics - Whether to track the analytics. Defaults to false.
 * @param cellPerColumn - The cell per column for the table. Defaults to an empty array.
 * @param sortConfig - The config for the sort. Defaults to an empty array.
 * @param externalSortManagement - Whether to use the external sort management. Defaults to false.
 * @param hasPagination - Whether the wrapper reserves room for a pagination footer under the table. Required: pass true when a pagination footer renders below, false otherwise (infinite scroll, no pagination).
 * @param bottomOffset - The bottom offset for the table. Defaults to 0.
 * @param itemIdKey - The key for the item id. Defaults to "id".
 * @param wrapperClassName - The class name for the wrapper. Defaults to undefined.
 * @param emptyState - The empty state for the table. Defaults to undefined.
 * @param onLoadMore - The callback to use for the load more. Defaults to undefined.
 * @param virtualize - Whether to virtualize the table. Defaults to false.
 * @param hideSettings - Whether to hide the column settings (cog) control. Defaults to false.
 * @param stickyRight - Predicate deciding which column sticks to the right edge. Defaults to `isStickyRight` (pins the last column). Pass `() => false` for narrow tables where pinning the last column would overlap earlier columns when the viewport shrinks.
 * @param activeRowId - Id of the row to highlight as active. Defaults to undefined.
 *
 * @returns
 */
const TableSimple = <T extends TableSimpleDataItem>({
  data,
  store,
  selectionStore,
  selectionOptions,
  shiftSelectionAction,
  ariaLabel,
  loading,
  rowsPerPage = 50,
  sortCallback,
  analyticsPage,
  filtersStorageKey,
  trackAnalytics,
  cellPerColumn,
  sortConfig,
  externalSortManagement,
  hasPagination,
  bottomOffset,
  itemIdKey = "id",
  wrapperClassName,
  emptyState,
  onLoadMore,
  virtualize = false,
  hideSettings = false,
  stickyRight,
  activeRowId,
}: TableSimpleProps<T>) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const columnIds = store((state) => state.columnsOrder.visible);
  const hasMoreSelectionOptions = (selectionOptions?.length ?? 0) > 1;

  const rowsProps = {
    data,
    store,
    selectionStore,
    hasMoreSelectionOptions,
    shiftSelectionAction,
    cellPerColumn,
    itemIdKey,
    tableRef,
    stickyRight,
    activeRowId,
  };

  return (
    <TableWrapper
      ref={tableRef}
      hasPagination={hasPagination}
      bottomOffset={bottomOffset}
      className={wrapperClassName}
    >
      <Table ariaLabel={ariaLabel}>
        <TableSimpleHead
          store={store}
          selectionStore={selectionStore}
          selectionOptions={selectionOptions}
          ariaLabel={ariaLabel}
          sortCallback={sortCallback}
          analyticsPage={analyticsPage}
          filtersStorageKey={filtersStorageKey}
          trackAnalytics={trackAnalytics}
          tableRef={tableRef}
          sortConfig={sortConfig}
          externalSortManagement={externalSortManagement}
          hideSettings={hideSettings}
          stickyRight={stickyRight}
        />
        {loading && <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />}
        {!loading && (
          <TableBody>
            {virtualize && <TableSimpleVirtualizedRows {...rowsProps} />}
            {!virtualize && <TableSimpleRows {...rowsProps} />}

            {onLoadMore && (
              <TableSimpleLoadMoreRow
                onLoadMore={onLoadMore}
                columnsCount={columnIds.length}
                rootMargin={LOAD_MORE_ROOT_MARGIN}
                scrollContainerRef={tableRef}
              />
            )}
          </TableBody>
        )}
      </Table>
      {!loading && data.length === 0 && emptyState}
    </TableWrapper>
  );
};

TableSimple.displayName = "DS.TableSimple";

export default memo(TableSimple) as typeof TableSimple;
